import React, { useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { App, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import { useWindowDimensions } from '../../hooks';
import {
  previewCompactBreakpoint,
  previewCompactButtonLeft,
  previewCompactButtonWidth,
} from '../../constants/gridConfig';
import { getPrimaryDark } from '../../constants/constants';
import { canUseWebEditor } from '../../utils/browser';
import { specificPdfErrorMessage } from '../../utils/pdfErrorMessage';
import { countUnavailablePlanningImages } from '../../utils/planningScreenshots';
import GenerateWebButton from '../../webviewer/components/GenerateWebButton/GenerateWebButton';


// Bouton d'une option d'export (aperçu/génération/archive). Pendant l'aperçu
// PDF, se transforme en bouton "retour au document" repositionné via portail
// à l'endroit exact où il est monté (cf. ref + PreviewPortal).
const GeneratePDFButton = ({ type, downLoadPdf }) => {
  const ref = useRef();
  const { t } = useTranslation();
  const previewRenderState = useStore(({ previewRenderState }) => previewRenderState);
  const PDFRenderState = useStore(({ PDFRenderState }) => PDFRenderState);
  const closePreview = useStore(({ closePreview }) => closePreview);
  // Même bleu que le token antd colorPrimary (cf. Layout.jsx) qui pilote le
  // Switch et les Collapse : mode-dépendant (navy proposal / violet template).
  const accentColor = useStore(({ currentDocument }) => getPrimaryDark(currentDocument?.type === 'template'));
  const { width: windowWidth } = useWindowDimensions();
  const isCompactPreview = windowWidth < previewCompactBreakpoint;

  const disabled = !!PDFRenderState;
  const loading = PDFRenderState === type;
  const isArchive = type === 'archive';
  const isPreview = type === 'preview';

  const getIcon = () => {
    if (isArchive) return <FontAwesomeIcon icon={icon({ name: 'cloud-arrow-down', style: 'light' })} style={{ marginRight: 8 }} />;
    if (isPreview) return <FontAwesomeIcon icon={icon({ name: 'eye', style: 'light' })} style={{ marginRight: 8 }} />;
    return <FontAwesomeIcon icon={icon({ name: 'file-pdf', style: 'light' })} style={{ marginRight: 8 }} />;
  };

  const getTitle = () => {
    if (isArchive) return 'archive_pdf';
    if (isPreview) return 'preview_pdf';
    return 'visualize_pdf';
  };

  const getPositions = useCallback(() => {
    if (!ref.current) return { top: null, left: null };
    return ref.current.getBoundingClientRect();
  }, []);

  const renderButton = (isForPreviewView) => {
    if (isForPreviewView && isPreview) {
      return (
        <Button
          className='style_btn_right_side'
          onClick={() => closePreview()}
          icon={<FontAwesomeIcon icon={icon({ name: 'arrow-left', style: 'light' })} style={{ marginRight: 8 }} />}
          loading={loading}
          disabled={disabled}
          style={{
            backgroundColor: '#fff',
            color: accentColor,
            borderColor: accentColor,
            ...(isCompactPreview ? { width: previewCompactButtonWidth } : {}),
            ...(disabled ? { opacity: 0.65 } : {}),
          }}
        >
          <span>{t('main-document')}</span>
        </Button>
      );
    }
    // En vue aperçu (isForPreviewView), le bouton flotte sur le fond assombri
    // du PDF : fond blanc plein (au lieu du ghost transparent), comme « Retour
    // au document », pour rester lisible dessus.
    return (
      <Button
        className='style_btn_right_side'
        onClick={() => downLoadPdf(type)}
        icon={getIcon()}
        ghost={!isForPreviewView}
        loading={loading}
        disabled={disabled}
        style={
          isForPreviewView
            ? {
                backgroundColor: '#fff',
                color: accentColor,
                borderColor: accentColor,
                ...(isCompactPreview ? { width: previewCompactButtonWidth } : {}),
                ...(disabled ? { opacity: 0.65 } : {}),
              }
            : {
                color: accentColor,
                borderColor: accentColor,
                '--btn-accent-color': accentColor,
                fontSize: 13,
                ...(disabled ? { backgroundColor: accentColor, opacity: 0.65, borderColor: accentColor } : {}),
              }
        }
      >
        <span>{t(getTitle())}</span>
      </Button>
    );
  };

  return (
    <>
      <div ref={ref}>{renderButton(false)}</div>
      {previewRenderState &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: getPositions().top,
              left: isCompactPreview ? previewCompactButtonLeft : getPositions().left,
            }}
          >
            {renderButton(true)}
          </div>,
          document.getElementById('PreviewPortal')
        )}
    </>
  );
};

// Contenu de l'onglet "Export" du panel de gauche (RAIL_ITEMS dans
// LeftSideRail) : liste directement les boutons d'export dans le panel, pas
// de popover (aperçu masqué en paysage, archive réservée opportunité/offre).
const ExportPanel = () => {
  const { t } = useTranslation();
  // `App.useApp()` et non `Modal.confirm` : la version statique rend hors du
  // thème et du contexte antd de l'application.
  const { modal: modalApi } = App.useApp();
  const isLandscape = useStore(({ landscape }) => landscape);
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const saveAndGeneratePdf = useStore(({ saveAndGeneratePdf }) => saveAndGeneratePdf);
  const resetPdfRender = useStore(({ resetPdfRender }) => resetPdfRender);
  const handleUnSelectBox = useStore(({ handleUnSelectBox }) => handleUnSelectBox);
  const handleUnSelectContainer = useStore(({ handleUnSelectContainer }) => handleUnSelectContainer);

  const currentProposal = useStore(({ currentProposal }) => currentProposal);

  const canArchive = currentDocument.type === 'opportunity' || currentDocument.type === 'offer';
  const canGenerateWeb = canUseWebEditor() && (currentDocument.type === 'opportunity' || currentDocument.type === 'offer');
  // Identité du document source (opportunité / offre) — `currentDocument` a
  // déjà exactement cette forme, elle est lue de l'URL au chargement.
  // Indispensable à la génération : c'est elle, et pas l'id de proposal, qui
  // adresse la route des variables.
  const documentIdentity = canGenerateWeb && currentDocument?.id
    ? { id: currentDocument.id, type: currentDocument.type }
    : null;

  // Un planning dont la capture est en cours ou en échec sortirait vide du PDF :
  // l'utilisateur choisit d'exporter quand même ou d'attendre. L'aperçu, lui,
  // sert justement à voir le document : pas de question.
  const confirmUnavailablePlannings = useCallback(async (type) => {
    if (type === 'preview') return true;
    const count = countUnavailablePlanningImages(useStore.getState().boxes);
    if (count === 0) return true;
    return modalApi.confirm({
      title: t('planning-export-unavailable-title'),
      content: t('planning-export-unavailable-content', { count }),
      okText: t('planning-export-unavailable-ok'),
      cancelText: t('planning-export-unavailable-cancel'),
      centered: true,
    });
  }, [modalApi, t]);

  const downLoadPdf = useCallback(async (type) => {
    if (!(await confirmUnavailablePlannings(type))) return;
    handleUnSelectBox();
    handleUnSelectContainer();
    const key = 'pdfKey';
    message.open({
      type: 'loading',
      content: type === 'preview' ? "Génération de l'aperçu en cours." : 'Création du PDF en cours.',
      duration: 0,
      key,
    });
    setTimeout(async () => {
      try {
        await saveAndGeneratePdf(type);
      } catch (error) {
        resetPdfRender();
        message.open({
          type: 'error',
          content:
            specificPdfErrorMessage(error.code) ??
            (type === 'preview' ? "Une erreur est survenue lors de la génération de l'aperçu." : "Une erreur est survenue lors de la génération du PDF."),
          duration: 2,
          key,
        });
      }
    }, 0);
  }, [confirmUnavailablePlannings, handleUnSelectBox, handleUnSelectContainer, saveAndGeneratePdf, resetPdfRender]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: 16, gap: 16 }}>
      {!isLandscape && <GeneratePDFButton type='preview' downLoadPdf={downLoadPdf} />}
      <GeneratePDFButton type='visualize' downLoadPdf={downLoadPdf} />
      {canArchive && <GeneratePDFButton type='archive' downLoadPdf={downLoadPdf} />}
      {canGenerateWeb && (
        <GenerateWebButton
          proposalId={currentProposal?.id}
          hasWebData={!!currentProposal?.has_web_data}
          /* Sans elle, la génération part avec un catalogue de variables VIDE :
             toutes les variables du site sont figées sous leur nom au lieu de
             leur valeur, et le menu « @ » de l'éditeur web répond « aucun
             document rattaché ». */
          documentIdentity={documentIdentity}
        />
      )}
    </div>
  );
};

export default ExportPanel;
